import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import '../../styles/Timeline.css';

const Timeline = ({ entityType, entityId }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (entityType && entityId) {
      fetchTimeline();
    }
  }, [entityType, entityId]);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/documents/timeline/${entityType}/${entityId}`);
      setTimeline(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      setError('Failed to load timeline data');
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  const timelineStats = useMemo(() => {
    const stats = timeline.reduce((acc, entry) => {
      acc.total++;
      acc[entry.action_type] = (acc[entry.action_type] || 0) + 1;
      return acc;
    }, { total: 0 });
    return stats;
  }, [timeline]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffTime = today - targetDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getActionIcon = (actionType) => {
    const icons = {
      create: '✨',
      edit: '✏️',
      status_change: '🔄',
      delete: '🗑️',
      upload: '📤',
      download: '📥',
      approve: '✅',
      reject: '❌'
    };
    return icons[actionType] || '📝';
  };

  const getActionTitle = (actionType) => {
    const titles = {
      create: 'Created',
      edit: 'Modified',
      status_change: 'Status Changed',
      delete: 'Deleted',
      upload: 'Uploaded',
      download: 'Downloaded',
      approve: 'Approved',
      reject: 'Rejected'
    };
    return titles[actionType] || actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getUserInitials = (name) => {
    if (!name) return 'SY';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="timeline-container">
        <div className="timeline-loading">
          <div className="timeline-spinner"></div>
          <div className="timeline-loading-text">Loading timeline...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="timeline-container">
        <div className="timeline-empty">
          <div className="timeline-empty-icon">⚠️</div>
          <div className="timeline-empty-title">Error Loading Timeline</div>
          <div className="timeline-empty-description">{error}</div>
          <button
            className="responsive-btn responsive-btn-primary"
            onClick={fetchTimeline}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="timeline-container">
      <div className="timeline-header">
        <h3 className="timeline-title">Activity Timeline</h3>
        <div className="timeline-stats" style={{ paddingLeft: '20px' }}>
          <div className="timeline-stat">
            <div className="timeline-stat-number">{timelineStats.total}</div>
            <div className="timeline-stat-label">Total</div>
          </div>
          {timelineStats.create && (
            <div className="timeline-stat">
              <div className="timeline-stat-number">{timelineStats.create}</div>
              <div className="timeline-stat-label">Created</div>
            </div>
          )}
          {timelineStats.status_change && (
            <div className="timeline-stat">
              <div className="timeline-stat-number">{timelineStats.status_change}</div>
              <div className="timeline-stat-label">Changes</div>
            </div>
          )}
        </div>
      </div>

      {timeline.length > 0 ? (
        <div className="timeline-content">
          <div className="timeline-line"></div>
          <div className="timeline-items">
            {timeline.map((entry, index) => (
              <div key={entry.id || index} className="timeline-item">
                <div className={`timeline-icon ${entry.action_type}`}>
                  {getActionIcon(entry.action_type)}
                </div>

                <div className="timeline-content-card">
                  <div className="timeline-action">
                    <div className="timeline-action-title">
                      {getActionTitle(entry.action_type)}
                    </div>
                    <div className={`timeline-action-badge ${entry.action_type}`}>
                      {entry.action_type.replace(/_/g, ' ')}
                    </div>
                  </div>

                  {entry.old_status && entry.new_status && (
                    <div className="timeline-status-change">
                      <div className="timeline-status-from">
                        📍 {entry.old_status}
                      </div>
                      <div className="timeline-status-arrow">→</div>
                      <div className="timeline-status-to">
                        🎯 {entry.new_status}
                      </div>
                    </div>
                  )}

                  {entry.remarks && (
                    <div className="timeline-remarks">
                      <div className="timeline-remarks-text">
                        {entry.remarks}
                      </div>
                    </div>
                  )}

                  <div className="timeline-meta">
                    <div className="timeline-user">
                      <div className="timeline-user-avatar">
                        {getUserInitials(entry.ChangedBy?.name)}
                      </div>
                      <div className="timeline-user-info">
                        <div className="timeline-user-name">
                          {entry.ChangedBy?.name || 'System'}
                        </div>
                        <div className="timeline-user-role">
                          {entry.ChangedBy?.role || 'Automated'}
                        </div>
                      </div>
                    </div>

                    <div className="timeline-date">
                      <div className="timeline-date-primary">
                        {formatDate(entry.created_at)}
                      </div>
                      <div className="timeline-date-secondary">
                        {formatTime(entry.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="timeline-empty">
          <div className="timeline-empty-icon">📋</div>
          <div className="timeline-empty-title">No Activity Yet</div>
          <div className="timeline-empty-description">
            This item doesn't have any timeline history yet. Activity will appear here as changes are made.
          </div>
        </div>
      )}
    </div>
  );
};

export default Timeline;